import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import cashReservesService from '../../services/cashReservesService';
import { fmtCurrency } from './helpers';

/**
 * TamaracTodoList — suggested actions that still need handling in Tamarac.
 *
 * The dashboard is read-only toward Tamarac, so Mark Reviewed captures two
 * kinds of Tamarac work: actions the user resolved in-app (a note or reserve
 * link that lives only in our database — the row carries the text to enter in
 * Tamarac by hand), and actions still open at review time (an Old Cash
 * Reserve, an end date to set — badged Unaddressed, since the fix itself
 * happens in Tamarac; dismiss the ones that are noise). Unaddressed rows
 * auto-complete once the daily analysis stops flagging the condition.
 *
 * Rows only ever change status. Exporting the list deletes nothing, and a
 * resolved row can always be reopened.
 */

const STATUS_LABELS = {
  completed: 'Completed',
  dismissed: 'Dismissed',
};

// Pre-migration rows carry no origin; only resolved-origin todos existed then.
const isUnaddressed = (todo) => todo.origin === 'outstanding';

const parseLinks = (todo) => {
  try {
    const links = JSON.parse(todo.mirror_links_json || '[]');
    return Array.isArray(links) ? links : [];
  } catch (err) {
    return [];
  }
};

const describeLinks = (links) =>
  links
    .map((link) => {
      const label = link.reserve_label || link.reserve_id || 'reserve';
      return link.allocated_amount != null
        ? `${label} (${fmtCurrency(link.allocated_amount)})`
        : label;
    })
    .join('; ');

// ARGB colors for the workbook, matched to the dashboard's Bootstrap palette.
const EXCEL_FAMILY_FILL = 'FFE9ECEF';   // gray-200 section band
const EXCEL_UNADDRESSED = 'FF9A6700';   // warning-emphasis
const EXCEL_MUTED = 'FF6C757D';         // secondary text
const EXCEL_DIMMED = 'FFADB5BD';        // gray-500, for completed/dismissed rows

const EXCEL_COLUMNS = [
  { header: 'Done', key: 'done', width: 6 },
  { header: 'Action', key: 'action', width: 28 },
  { header: 'Origin', key: 'origin', width: 20 },
  { header: 'Detail', key: 'detail', width: 50 },
  { header: 'Note to enter in Tamarac', key: 'note', width: 40 },
  { header: 'Linked reserves', key: 'links', width: 30 },
  { header: 'Transaction ID', key: 'transactionId', width: 14 },
  { header: 'Captured', key: 'captured', width: 22 },
  { header: 'Status', key: 'status', width: 12 },
];

// Group the rows the way the user works through Tamarac: family by family,
// account by account. Map preserves insertion order, so sorting the keys once
// keeps families and accounts alphabetical while todos keep their list order.
const groupByFamilyAndAccount = (todos) => {
  const families = new Map();
  todos.forEach((todo) => {
    const familyKey =
      todo.family_name || todo.family_id || todo.account_name || todo.account_number;
    if (!families.has(familyKey)) families.set(familyKey, new Map());
    const accounts = families.get(familyKey);
    const accountKey = `${todo.account_name || 'Account'} (${todo.account_number})`;
    if (!accounts.has(accountKey)) accounts.set(accountKey, []);
    accounts.get(accountKey).push(todo);
  });
  return [...families.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([familyName, accounts]) => [
      familyName,
      [...accounts.entries()].sort(([a], [b]) => a.localeCompare(b)),
    ]);
};

const exportTodosToExcel = async (todos) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Tamarac Todo List');
  worksheet.columns = EXCEL_COLUMNS;
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).alignment = { vertical: 'top', wrapText: true };

  groupByFamilyAndAccount(todos).forEach(([familyName, accountEntries]) => {
    const familyRow = worksheet.addRow({ done: String(familyName) });
    worksheet.mergeCells(familyRow.number, 1, familyRow.number, EXCEL_COLUMNS.length);
    familyRow.font = { bold: true, size: 12 };
    familyRow.getCell(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_FAMILY_FILL },
    };

    accountEntries.forEach(([accountLabel, accountTodos]) => {
      const accountRow = worksheet.addRow({ done: `  ${accountLabel}` });
      worksheet.mergeCells(accountRow.number, 1, accountRow.number, EXCEL_COLUMNS.length);
      accountRow.font = { bold: true };

      accountTodos.forEach((todo) => {
        const row = worksheet.addRow({
          done: '',
          action: todo.label || todo.action,
          origin: isUnaddressed(todo) ? 'Unaddressed' : 'Resolved in dashboard',
          detail: todo.detail || '',
          note: todo.mirror_note || '',
          links: describeLinks(parseLinks(todo)),
          transactionId: todo.transaction_id || '',
          captured: [
            todo.created_at ? new Date(todo.created_at).toLocaleDateString() : '',
            todo.source_reviewed_by || '',
          ]
            .filter(Boolean)
            .join(' · '),
          status: STATUS_LABELS[todo.status] || 'Open',
        });
        row.alignment = { vertical: 'top', wrapText: true };
        if (todo.status !== 'open') {
          // Dim the whole row (row.font would be clobbered by the cell
          // overrides below, so resolved rows get no per-cell styling).
          row.font = { color: { argb: EXCEL_DIMMED } };
        } else if (isUnaddressed(todo)) {
          row.getCell('origin').font = { bold: true, color: { argb: EXCEL_UNADDRESSED } };
        } else {
          row.getCell('origin').font = { color: { argb: EXCEL_MUTED } };
        }
      });
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `tamarac_todos_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

// ─── one todo row ─────────────────────────────────────────────────────────────
const TodoRow = ({ todo, busy, onResolve, onReopen }) => {
  const isOpen = todo.status === 'open';
  const links = parseLinks(todo);
  return (
    <tr className={isOpen ? undefined : 'text-muted'}>
      <td>
        <div className="fw-semibold">{todo.account_name || todo.account_number}</div>
        <div className="small text-muted">
          {todo.account_number}
          {todo.family_name && todo.family_name !== todo.account_name
            ? ` · ${todo.family_name}`
            : ''}
        </div>
      </td>
      <td>
        <div className="fw-semibold">
          {todo.label || todo.action}
          {isUnaddressed(todo) && (
            <span
              className="badge bg-warning-subtle text-warning-emphasis ms-2"
              title="Was still open when the account was marked reviewed — the fix itself happens in Tamarac. Dismiss it if it's noise."
            >
              Unaddressed
            </span>
          )}
        </div>
        {todo.detail && <div className="small text-muted">{todo.detail}</div>}
      </td>
      <td>
        {todo.mirror_note ? (
          <div>{todo.mirror_note}</div>
        ) : (
          <span className="text-muted">—</span>
        )}
        {links.length > 0 && (
          <div className="small text-muted" title="Reserves the transaction was linked to">
            <i className="bi bi-link-45deg me-1"></i>
            {describeLinks(links)}
          </div>
        )}
      </td>
      <td>
        <div className="small text-muted">
          {todo.created_at ? new Date(todo.created_at).toLocaleDateString() : '—'}
          {todo.source_reviewed_by ? ` · ${todo.source_reviewed_by}` : ''}
        </div>
        {!isOpen && (
          <div className="small">
            {STATUS_LABELS[todo.status] || todo.status}
            {todo.resolution_source === 'auto_ingest'
              ? ' (auto)'
              : todo.resolved_by
                ? ` · ${todo.resolved_by}`
                : ''}
          </div>
        )}
      </td>
      <td className="text-end text-nowrap">
        {isOpen ? (
          <>
            <button
              type="button"
              className="btn btn-sm btn-outline-success me-1"
              disabled={busy}
              onClick={() => onResolve(todo.todo_id, 'completed')}
              title="The change has been made in Tamarac — take this off the list"
            >
              Complete
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={busy}
              onClick={() => onResolve(todo.todo_id, 'dismissed')}
              title="This never needs mirroring into Tamarac"
            >
              Dismiss
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={busy}
            onClick={() => onReopen(todo.todo_id)}
            title="Put this todo back on the open list"
          >
            Undo
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── list manager ─────────────────────────────────────────────────────────────
const TamaracTodoList = ({ onClose, onChanged, initialTodos = null }) => {
  // Seeded from whatever the dashboard already loaded to badge its button, so
  // opening the modal paints immediately and revalidates behind that.
  const [todos, setTodos] = useState(initialTodos || []);
  const [isLoading, setIsLoading] = useState(initialTodos === null);
  const [error, setError] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const data = await cashReservesService.getTamaracTodos();
      setTodos(data.todos || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load the Tamarac todo list');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // Captured once: whether the dashboard handed us rows to paint decides
  // whether this first fetch is a load or a revalidation.
  const seeded = useRef(initialTodos !== null);

  useEffect(() => {
    load({ silent: seeded.current });
  }, [load]);

  const openTodos = useMemo(
    () => todos.filter((todo) => todo.status === 'open'),
    [todos]
  );
  const rows = showResolved ? todos : openTodos;

  const runAction = async (todoId, action, failureMessage) => {
    if (busyId) {
      setError('Another todo update is already in progress. Please wait.');
      return;
    }
    setBusyId(todoId);
    setError('');
    try {
      await action();
      await load({ silent: true });
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || failureMessage);
    } finally {
      setBusyId((current) => (current === todoId ? '' : current));
    }
  };

  const handleResolve = (todoId, resolution) =>
    runAction(
      todoId,
      () => cashReservesService.resolveTamaracTodo(todoId, { resolution }),
      'Failed to resolve the todo'
    );

  const handleReopen = (todoId) =>
    runAction(
      todoId,
      () => cashReservesService.reopenTamaracTodo(todoId),
      'Failed to reopen the todo'
    );

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      await exportTodosToExcel(rows);
    } catch (err) {
      setError(err.message || 'Failed to build the Excel export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="cash-reserve-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="card shadow"
        style={{ maxWidth: '1400px', width: '100%', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="bi bi-list-check me-2 text-primary"></i>
            Tamarac Todo List
            {openTodos.length > 0 && (
              <span className="badge bg-primary ms-2">{openTodos.length}</span>
            )}
          </span>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>
        {/* minHeight: 0 lets the body shrink below its content (a flex child's
            default min-height is auto), so the card's 85vh cap actually holds
            and the table scrolls instead of spilling out of the modal. */}
        <div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>
          <p className="text-muted small">
            Suggested actions captured when an account is marked reviewed. Rows with a note are
            actions you resolved on the dashboard — the note still needs entering in Tamarac by
            hand. Rows badged <span className="fw-semibold">Unaddressed</span> were still open at
            review time; their fix happens in Tamarac itself, and they check themselves off once
            the daily analysis stops flagging them — or dismiss the ones that are just noise.
            Exporting changes nothing.
          </p>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="showResolvedTodos"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="showResolvedTodos">
                Show completed and dismissed
              </label>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={rows.length === 0 || exporting}
              onClick={handleExport}
              title="Download the rows shown below as an Excel worklist, grouped by family"
            >
              <i className={`bi ${exporting ? 'bi-hourglass-split' : 'bi-download'} me-1`}></i>
              {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-3 text-muted">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <i className="bi bi-inbox me-2"></i>
              {todos.length === 0
                ? 'Nothing is waiting to be mirrored into Tamarac.'
                : 'Nothing is outstanding — every todo has been cleared.'}
            </div>
          ) : (
            /* The rows scroll on their own so the intro, filter and Export
               button stay in reach; the header row sticks inside the same
               scroll container. */
            <div className="table-responsive" style={{ overflowY: 'auto', minHeight: 0 }}>
              <table className="table table-sm align-middle mb-0">
                <thead
                  className="table-light"
                  style={{ position: 'sticky', top: 0, zIndex: 2 }}
                >
                  <tr>
                    <th>Account</th>
                    <th>Action Taken</th>
                    <th>Note to Mirror</th>
                    <th>Captured</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((todo) => (
                    <TodoRow
                      key={todo.todo_id}
                      todo={todo}
                      busy={busyId === todo.todo_id}
                      onResolve={handleResolve}
                      onReopen={handleReopen}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TamaracTodoList;
